import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FaXTwitter } from 'react-icons/fa6';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { ImageUploader } from 'root/src/client/v2/_pages/add-campaign/redesign/components/MediaUploaders';
const URL_REGEX = /^(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,11}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)$/;
const HANDLE_REGEX = /^[A-Za-z0-9_]{1,15}$/;
// Backend stores a twitter.com link; the UI only takes the handle.
export const extractHandle = (value) => (value !== null && value !== void 0 ? value : '')
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/^(twitter|x)\.com\//, '')
    .replace(/\/$/, '')
    .trim();
export const publisherSchema = z.object({
    brandName: z.string().min(1, 'Publisher name is required'),
    brandWebsite: z.string().min(1, 'Website is required').regex(URL_REGEX, 'Enter a valid URL, for example https://example.com'),
    twitterHandle: z.string().optional().refine(value => !value || HANDLE_REGEX.test(value), 'Use up to 15 letters, numbers or underscore'),
    brandLogoUrl: z.string().optional(),
});
// Shared react-hook-form setup for both the Publisher Settings dialog section and the after-login
// publisher settings modal: same schema, same seeding from the stored brand settings.
export const usePublisherSettingsForm = (current) => {
    var _a, _b, _c;
    const form = useForm({
        resolver: zodResolver(publisherSchema),
        defaultValues: { brandName: '', brandWebsite: '', twitterHandle: '', brandLogoUrl: '' },
    });
    // Re-seed when the stored brand settings load/change.
    const currentHandle = extractHandle(current === null || current === void 0 ? void 0 : current.brandTwitter);
    const currentKey = `${(_a = current === null || current === void 0 ? void 0 : current.brandName) !== null && _a !== void 0 ? _a : ''}|${(_b = current === null || current === void 0 ? void 0 : current.brandWebsite) !== null && _b !== void 0 ? _b : ''}|${currentHandle}|${(_c = current === null || current === void 0 ? void 0 : current.brandLogoUrl) !== null && _c !== void 0 ? _c : ''}`;
    useEffect(() => {
        var _a, _b, _c;
        form.reset({
            brandName: (_a = current === null || current === void 0 ? void 0 : current.brandName) !== null && _a !== void 0 ? _a : '',
            brandWebsite: (_b = current === null || current === void 0 ? void 0 : current.brandWebsite) !== null && _b !== void 0 ? _b : '',
            twitterHandle: currentHandle,
            brandLogoUrl: (_c = current === null || current === void 0 ? void 0 : current.brandLogoUrl) !== null && _c !== void 0 ? _c : '',
        });
    }, [currentKey]);
    return form;
};
// Turns the form values into the SET_BRAND_SETTINGS payload. The logo is only sent when set; the server
// reprocesses it only when it differs from the stored one.
export const buildBrandSettingsPayload = (values) => {
    var _a;
    const handle = (_a = values.twitterHandle) === null || _a === void 0 ? void 0 : _a.trim();
    return Object.assign({ brandName: values.brandName, brandWebsite: values.brandWebsite, brandTwitter: handle ? `twitter.com/${handle}` : '' }, (values.brandLogoUrl ? { brandLogoUrl: values.brandLogoUrl } : {}));
};
// A required field: label plus a pink asterisk (matches the app-wide RequiredMark).
const RequiredLabel = ({ children }) => (<span>{children} <span className="text-pink">*</span></span>);
// An optional field: label plus a muted "(optional)" (matches the redesign form convention).
const OptionalLabel = ({ children }) => (<span>{children} <span className="font-normal text-muted-foreground/60">(optional)</span></span>);
// The four publisher fields (logo, name, website, twitter), shared by the settings dialog section and
// the after-login modal so both stay in sync. The caller owns the form element and submit button.
export const PublisherSettingsFields = ({ register, errors, brandLogoUrl, onLogoUploaded }) => {
    var _a, _b, _c;
    return (<>
		<Field label={<OptionalLabel>Brand logo</OptionalLabel>} info="Square PNG or SVG, at least 130px. Shown on your drops and to creators.">
			<ImageUploader value={brandLogoUrl} onUploaded={result => onLogoUploaded(result.url.replace('https://', ''))}/>
		</Field>
		<Field label={<RequiredLabel>Publisher name</RequiredLabel>} htmlFor="brand-name" error={(_a = errors.brandName) === null || _a === void 0 ? void 0 : _a.message}>
			<Input id="brand-name" error={!!errors.brandName} {...register('brandName')}/>
		</Field>
		<Field label={<RequiredLabel>Website</RequiredLabel>} htmlFor="brand-website" error={(_b = errors.brandWebsite) === null || _b === void 0 ? void 0 : _b.message}>
			<Input id="brand-website" placeholder="https://example.com" error={!!errors.brandWebsite} {...register('brandWebsite')}/>
		</Field>
		<Field label={<OptionalLabel>Twitter / X</OptionalLabel>} htmlFor="brand-twitter" error={(_c = errors.twitterHandle) === null || _c === void 0 ? void 0 : _c.message}>
			<div className={cn('flex h-9 items-center gap-1.5 rounded-lg border border-solid bg-white/[0.03] px-1.5 transition-colors focus-within:border-primary/30', errors.twitterHandle ? 'border-destructive/60' : 'border-white/[0.06]')}>
				<span className="flex shrink-0 select-none items-center gap-1 rounded-md bg-white/[0.06] px-2 py-1 text-xs font-medium text-muted-foreground">
					<FaXTwitter className="h-3 w-3"/>
					x.com/
				</span>
				<input id="brand-twitter" placeholder="username" className="h-full w-full bg-transparent pr-1.5 text-sm outline-none placeholder:text-muted-foreground/30" {...register('twitterHandle')}/>
			</div>
		</Field>
	</>);
};
